import { Module } from 'vuex';

import { RootState } from '@/store';
import { customersService } from '@/services/customers/customers.service';
import { customerEventsService } from '@/services/customer-events/customer-events.service';
import { Customer, CustomerContact, CustomerCountry, CustomerStats } from '@/services/customers/customers.types';
import { CustomerEvent } from '@/services/customer-events/customer-events.types';

export interface CustomerState {
  customer: Customer;
  customerLoading: boolean;
  paymentConditions: string[];
  countries: CustomerCountry[];
  stats: CustomerStats | null;
  events: CustomerEvent[];
  eventsLoading: boolean;
  eventTypes: { code: string; label: string }[];
}

const state: CustomerState = {
  customer: null,
  customerLoading: false,
  paymentConditions: [],
  countries: [],
  stats: null,
  events: [],
  eventsLoading: false,
  eventTypes: []
};

const options: Module<CustomerState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setCustomer: ({ commit, dispatch }, customer: Customer | string | number): Promise<void> => {
      if (customer == null) {
        commit('customer', null);
        return;
      }
      if (typeof customer === 'number' || typeof customer === 'string') {
        return dispatch('getCustomer', customer);
      }
      /* If we got a list row, fetch the full enriched record */
      if ((customer as Customer).id) {
        commit('customer', customer);
        return dispatch('getCustomer', (customer as Customer).id);
      }
      commit('customer', customer);
    },
    getCustomer: ({ commit, dispatch }, customerId: number | string): Promise<void> => {
      commit('customerLoading', true);
      return customersService
        .get(customerId)
        .then(customer => customer && commit('customer', customer))
        .then(() => dispatch('listEvents', customerId))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('customerLoading', false));
    },
    newCustomer: ({ commit }): Promise<void> => {
      commit('customer', {});
      return Promise.resolve();
    },
    createCustomer: ({ commit, dispatch }, customer: Partial<Customer>): Promise<Customer> => {
      commit('customerLoading', true);
      return customersService
        .create(customer)
        .then(created => {
          if (created) commit('customer', created);
          return created;
        })
        .then(created => {
          dispatch('alert/pushSuccess', 'Client créé avec succès !', { root: true });
          return created;
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          return null;
        })
        .finally(() => commit('customerLoading', false));
    },
    updateCustomer: ({ commit, dispatch }, customer: Partial<Customer>): Promise<Customer> => {
      commit('customerLoading', true);
      return customersService
        .update(customer.id, customer)
        .then(updated => {
          if (updated) commit('customer', updated);
          return updated;
        })
        .then(updated => {
          dispatch('alert/pushSuccess', 'Client mis à jour !', { root: true });
          return updated;
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          return null;
        })
        .finally(() => commit('customerLoading', false));
    },
    deleteCustomer: ({ commit, dispatch }, customerId: number | string): Promise<void> => {
      commit('customerLoading', true);
      return customersService
        .delete(customerId)
        .then(() => commit('customer', null))
        .then(() => dispatch('alert/pushSuccess', 'Client supprimé !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('customerLoading', false));
    },
    archiveCustomer: ({ commit, dispatch }, customerId: number | string): Promise<void> => {
      commit('customerLoading', true);
      return customersService
        .archive(customerId)
        .then(updated => {
          commit('customer', updated);
          dispatch('alert/pushSuccess', 'Client archivé !', { root: true });
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('customerLoading', false));
    },
    blockCustomer: (
      { commit, dispatch },
      customerId: number | string
    ): Promise<{ cancelled_orders_count: number; cancelled_order_lines_count: number } | null> => {
      commit('customerLoading', true);
      return customersService
        .block(customerId)
        .then(resp => {
          commit('customer', resp.customer);
          return resp.blocking;
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          return null;
        })
        .finally(() => commit('customerLoading', false));
    },
    unblockCustomer: ({ commit, dispatch }, customerId: number | string): Promise<void> => {
      commit('customerLoading', true);
      return customersService
        .unblock(customerId)
        .then(updated => {
          commit('customer', updated);
          dispatch('alert/pushSuccess', 'Client débloqué !', { root: true });
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('customerLoading', false));
    },
    unflagBadPayer: ({ commit, dispatch }, customerId: number | string): Promise<void> => {
      commit('customerLoading', true);
      return customersService
        .unflagBadPayer(customerId)
        .then(updated => {
          commit('customer', updated);
          dispatch('alert/pushSuccess', 'Statut "Mauvais payeur" retiré !', { root: true });
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('customerLoading', false));
    },
    toggleCustomerFavorite: ({ dispatch }, customerId: number | string): Promise<void> => {
      return customersService
        .favorite(customerId)
        .then(() => dispatch('table/listRows', null, { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    loadPaymentConditions: ({ commit, dispatch, state }): Promise<void> => {
      if (state.paymentConditions?.length) return Promise.resolve();
      return customersService
        .paymentConditions()
        .then(values => commit('paymentConditions', values || []))
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    loadCountries: ({ commit, dispatch, state }): Promise<void> => {
      if (state.countries?.length) return Promise.resolve();
      return customersService
        .countries()
        .then(values => commit('countries', values || []))
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    loadStats: ({ commit, dispatch }): Promise<void> => {
      return customersService
        .stats()
        .then(values => commit('stats', values))
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    clearCustomer: ({ commit }): void => {
      commit('customer', null);
      commit('events', []);
    },

    /* Contacts (operations on the selected customer) */
    createContact: ({ state, commit, dispatch }, contact: Partial<CustomerContact>): Promise<void> => {
      if (!state.customer?.id) return Promise.resolve();
      return customersService
        .createContact(state.customer.id, contact)
        .then(created => {
          if (!created) return;
          commit('customer', {
            ...state.customer,
            contacts: [...(state.customer.contacts || []), created]
          });
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    updateContact: ({ state, commit, dispatch }, contact: CustomerContact): Promise<void> => {
      if (!state.customer?.id || !contact?.id) return Promise.resolve();
      return customersService
        .updateContact(state.customer.id, contact.id, contact)
        .then(updated => {
          if (!updated) return;
          commit('customer', {
            ...state.customer,
            contacts: (state.customer.contacts || []).map(c => (c.id === updated.id ? updated : c))
          });
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    deleteContact: ({ state, commit, dispatch }, contactId: number): Promise<void> => {
      if (!state.customer?.id || !contactId) return Promise.resolve();
      return customersService
        .deleteContact(state.customer.id, contactId)
        .then(() => {
          commit('customer', {
            ...state.customer,
            contacts: (state.customer.contacts || []).filter(c => c.id !== contactId)
          });
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },

    /* Events */
    loadEventTypes: ({ commit, dispatch, state }): Promise<void> => {
      if (state.eventTypes?.length) return Promise.resolve();
      return customerEventsService
        .listTypes()
        .then(types => commit('eventTypes', types || []))
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    listEvents: ({ commit, dispatch }, customerId: number | string): Promise<void> => {
      if (!customerId) return Promise.resolve();
      commit('eventsLoading', true);
      return customerEventsService
        .listForCustomer(customerId)
        .then(events => commit('events', events || []))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('eventsLoading', false));
    },
    createEvent: ({ state, commit, dispatch }, customerEvent: Partial<CustomerEvent>): Promise<void> => {
      if (!state.customer?.id) return Promise.resolve();
      return customerEventsService
        .create(state.customer.id, customerEvent)
        .then(created => {
          if (!created) return;
          commit('events', [created, ...state.events]);
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    updateEvent: (
      { state, commit, dispatch },
      { eventId, payload }: { eventId: number; payload: Partial<CustomerEvent> }
    ): Promise<void> => {
      if (!eventId) return Promise.resolve();
      return customerEventsService
        .update(eventId, payload)
        .then(updated => {
          if (!updated) return;
          commit(
            'events',
            state.events.map(e => (e.id === eventId ? { ...e, ...updated } : e))
          );
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    deleteEvent: ({ state, commit, dispatch }, eventId: number): Promise<void> => {
      if (!eventId) return Promise.resolve();
      return customerEventsService
        .delete(eventId)
        .then(() =>
          commit(
            'events',
            state.events.filter(e => e.id !== eventId)
          )
        )
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    }
  },
  mutations: {
    customer: (state, customer) => (state.customer = customer),
    customerLoading: (state, loading) => (state.customerLoading = loading),
    paymentConditions: (state, values) => (state.paymentConditions = values),
    countries: (state, values) => (state.countries = values),
    stats: (state, values) => (state.stats = values),
    events: (state, events) => (state.events = events),
    eventsLoading: (state, loading) => (state.eventsLoading = loading),
    eventTypes: (state, types) => (state.eventTypes = types)
  }
};

export default options;
